import { queryOptions, keepPreviousData } from '@tanstack/react-query';
import { withPaginationSchema, parseSearchParams } from '@/api/utils';
import { PaginationParams } from '@/api/types';
import api from '../api';
import { parseISO, format } from 'date-fns';
import {
  PatientUnionSchema,
  PatientListUnionSchema,
  FullPatient,
  PatientListItem,
  AnonymizedPatient,
  Patient,
  ExternalIdValidationSchema,
  ExternalIdValidation,
} from '@/api/schema/patient';

export type { FullPatient, PatientListItem, AnonymizedPatient, Patient, ExternalIdValidation };

type PatientsParams = {
  search?: string;
  status?: string;
  dateOfBirth?: string;
  dateOfBirthYear?: string;
  sex?: string;
  lastName?: string;
  subjectHumanReadableId?: string;
  externalId?: string;
  name?: string;
  [key: string]: string | number | boolean | undefined;
} & PaginationParams;

function parseParams(params: PatientsParams) {
  const parsedParams = {
    ...params,
    dateOfBirth: params.dateOfBirth ? format(parseISO(params.dateOfBirth), 'yyyy-MM-dd') : undefined,
  };
  return parseSearchParams(parsedParams);
}

export function getPatientsQueryOptions(params?: PatientsParams) {
  return queryOptions({
    queryKey: [ 'patients', params ],
    queryFn: async () => {
      if (!params) throw new Error('Params are required');

      const response = await api.get('patients', {
        searchParams: parseParams(params),
      });
      return withPaginationSchema(PatientListUnionSchema).parse(await response.json());
    },
    placeholderData: keepPreviousData,
  });
}

export function getPatientQueryOptions(id: string) {
  return queryOptions({
    queryKey: [ 'patients', id ],
    queryFn: async () => {
      const response = await api.get(`patients/${id}`);
      return PatientUnionSchema.parse(await response.json());
    },
  });
}

export function getExternalIdValidationOptions(externalId: string) {
  return queryOptions({
    queryKey: [ 'patients', 'validate-external-id', externalId ],
    queryFn: async () => {
      const response = await api.get('patients/validate-external-id', {
        searchParams: { externalId },
      });
      return ExternalIdValidationSchema.parse(await response.json());
    },
    staleTime: 30_000,
  });
}
